/**
 * 一覧の見え方（並べ替え・絞り込み）を、その端末に覚えておく。
 *
 * 1 本開いて戻ると選び直しになるのが元の不満である。React の state は
 * 画面を出し直せば初期値へ戻るので、選んだものを localStorage に残す。
 *
 * ## 置くのは見え方だけ
 *
 * トークンは**置かない**（`token-store.ts` のとおり、XSS で読める場所に
 * 置かない）。ここに入るのは並び順や絞り込みの選択肢のような、漏れても
 * 何も起きない値だけである。会社をまたいで共有されても困らない値に限る。
 *
 * ## 知らない値は捨てる
 *
 * 読んだ値が今の選択肢に無ければ、既定へ戻す。選択肢を減らした後も古い値が
 * 残るので、そのまま信じると**画面に無い条件で問い合わせる**ことになる。
 *
 * ## 書けなくても画面は動く
 *
 * private モードや storage を塞いだ設定では、読み書きそのものが例外を
 * 投げうる。覚えられないだけで、画面は普通に使えるようにしておく。
 */

import { useCallback, useState } from "react";

const PREFIX = "visumo.admin.view";

function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function storedChoiceKey(name: string): string {
  return `${PREFIX}.${name}`;
}

/** 覚えてある選択。**選択肢に無い値は無かったことにする。** */
export function readStoredChoice<T extends string>(
  name: string,
  allowed: readonly T[],
): T | null {
  let raw: string | null;
  try {
    raw = safeLocalStorage()?.getItem(storedChoiceKey(name)) ?? null;
  } catch {
    return null;
  }
  return allowed.find((value) => value === raw) ?? null;
}

export function writeStoredChoice(name: string, value: string): void {
  try {
    safeLocalStorage()?.setItem(storedChoiceKey(name), value);
  } catch {
    // 覚えられないだけ。画面は動かす
  }
}

/**
 * 覚えておく選択を持つ state。
 *
 * 初回だけ storage を読む（描き直すたびに読むと、別のタブで変えた値が
 * 入力の途中で割り込む）。
 */
export function useStoredChoice<T extends string>(
  name: string,
  allowed: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(
    () => readStoredChoice(name, allowed) ?? fallback,
  );
  const choose = useCallback(
    (next: T) => {
      setValue(next);
      writeStoredChoice(name, next);
    },
    [name],
  );
  return [value, choose];
}
